import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingCart, Tag } from "lucide-react";
import { Button } from "./ui/button";
import { fetchFeaturedProducts } from "../lib/api";

const fmtEur = (val) => `€ ${Number(val || 0).toFixed(2)}`;
const STOCK_LABELS = {
  in_stock: "Op voorraad",
  out_of_stock: "Niet op voorraad",
  backorder: "Backorder",
  "n/a": "Op aanvraag",
};
const STOCK_COLORS = {
  in_stock: "text-green-700",
  out_of_stock: "text-red-600",
  backorder: "text-amber-700",
  "n/a": "text-gray-500",
};

function effectivePrice(p) {
  if (p?.sale_price && Number(p.sale_price) > 0) return Number(p.sale_price);
  if (p?.regular_price && Number(p.regular_price) > 0) return Number(p.regular_price);
  return null;
}

export default function FeaturedProducts({
  limit = 3,
  title = "Populaire producten voor uw zending",
  subtitle = "Bestel verhuisdozen en cargo-benodigdheden direct via de 5999Cargo webshop.",
  ctaText = "Bekijk alle producten",
  ctaLink = "/webshop",
  selection = "featured", // featured | latest | manual (manual TBD)
}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // selection currently respected at backend level only for "featured"
        // (latest = same endpoint, will be extended later if needed).
        const data = await fetchFeaturedProducts(limit);
        if (alive) setProducts(data);
      } catch (_) {
        // Soft-fail — homepage must never break on a webshop hiccup
        if (alive) setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [limit, selection]);

  if (!loading && products.length === 0) return null;

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="home-featured-products">
      <div className="container-x">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <span className="eyebrow">5999Cargo Webshop</span>
            <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-3 max-w-2xl">
              {title}
            </h2>
            <p className="text-ink-500 mt-3 max-w-xl text-[15px] leading-relaxed">
              {subtitle}
            </p>
          </div>
          <Link
            to={ctaLink}
            data-testid="home-featured-view-all"
            className="btn-dark self-start"
          >
            {ctaText} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-featured-loading">
            {[...Array(limit)].map((_, i) => (
              <div key={i} className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-featured-grid">
            {products.map((p, idx) => {
              const price = effectivePrice(p);
              const stockKey = p.stock_status || "n/a";
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="card-premium flex flex-col overflow-hidden group"
                  data-testid={`home-featured-${p.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.image_alt || p.name}
                        title={p.image_title || p.name}
                        loading="lazy"
                        className="w-full h-full object-contain p-5 group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid={`home-featured-image-${p.slug}`}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                        <Tag className="w-10 h-10 mb-2 text-brand-500/30" />
                        <span className="text-[10px] uppercase tracking-wider font-bold text-brand-500/40">5999Cargo</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    {p.category?.name && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-brand-500 font-chivo font-bold">
                        {p.category.name}
                      </span>
                    )}
                    <h3 className="font-chivo font-bold text-xl text-brand-900 mt-1.5 leading-tight">
                      {p.name}
                    </h3>
                    {p.short_description && (
                      <p className="text-[14px] text-ink-500 mt-2.5 leading-relaxed line-clamp-2 flex-1">
                        {p.short_description}
                      </p>
                    )}
                    <div className="mt-5 flex items-center justify-between">
                      {price !== null ? (
                        <span className="font-chivo font-black text-2xl text-brand-900 font-mono-num" data-testid={`home-featured-price-${p.slug}`}>
                          {fmtEur(price)}
                        </span>
                      ) : (
                        <span className="text-sm text-ink-400">Op aanvraag</span>
                      )}
                      <span className={`text-xs font-bold uppercase tracking-wider ${STOCK_COLORS[stockKey]}`}>
                        {STOCK_LABELS[stockKey] || stockKey}
                      </span>
                    </div>
                    <Link
                      to="/webshop"
                      data-testid={`home-featured-cta-${p.slug}`}
                      className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-chivo font-bold uppercase text-xs tracking-wide transition-all shadow-brand-soft hover:shadow-brand-glow active:scale-[0.98]"
                    >
                      <ShoppingCart className="w-4 h-4" /> Bekijk in webshop
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
